import { clientConfig } from '@/config/clientConfig';
import { useAuth } from '@/domains/auth';
import type { BulkActionResponse } from '@/domains/jobs/api/workOrdersApi';
import { BulkActionResultModal } from '@/domains/jobs/components/BulkActionResultModal';
import { BulkActionStepperModal } from '@/domains/jobs/components/BulkActionStepperModal';
import type {
  BulkActionMode,
  BulkActionStepperSubmitItem,
} from '@/domains/jobs/components/BulkActionStepperModal';
import { BulkActionsBar } from '@/domains/jobs/components/BulkActionsBar';
import { RequestMoreInfoButton } from '@/domains/jobs/components/RequestMoreInfoButton';
import { RequestMoreInfoOrReplyModal } from '@/domains/jobs/components/RequestMoreInfoOrReplyModal';
import type { BulkJobInput } from '@/domains/jobs/hooks/useWorkOrderActions';
import { uploadJobDocuments } from '@/domains/jobs/lib/uploadJobDocuments';
import { PageHeader } from '@/shared/components/PageHeader';
import { Pagination } from '@/shared/components/Pagination';
import { useUrlFilters } from '@/shared/hooks/useUrlFilters';
import { cn } from '@/shared/lib/cn';
import { formatMinutesCompact } from '@/shared/lib/duration-utils';
import { downloadBlob, exportDateStamp } from '@/shared/lib/export-utils';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { AlertCircle, Download, Eye, FileText, Loader2, Search as SearchIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  createExportTemplate,
  deleteExportTemplate,
  getExportTemplates,
} from '../api/exportTemplateApi';
import type { ExportTemplate } from '../api/exportTemplateApi';
import type {
  JobExportField,
  JobListFilterParams,
  JobListSortBy,
  JobListSortOrder,
} from '../api/jobListApi';
import { downloadJobListExport, getJobExportFields } from '../api/jobListApi';
import { JOBS_PAGE_SIZE, JOB_LIST_DEFAULTS } from '../constants/jobListDefaults';
import { getStatusRowAccent } from '../constants/statusRowAccent';
import { getTabFilter, tabImposesStatus } from '../constants/tabConfig';
import { useJobList } from '../hooks/useJobList';
import { useJobTabCounts } from '../hooks/useJobTabCounts';
import {
  useAcceptWorkOrders,
  useRejectWorkOrders,
  useReplyMoreInfoWorkOrders,
  useRequestMoreInfoWorkOrders,
} from '../hooks/useWorkOrderActions';
import { useWorkOrderStatusList } from '../hooks/useWorkOrderStatusList';
import { formatRequiredBy } from '../lib/formatRequiredBy';
import type { WorkOrder } from '../types/workOrder';
import { ExportFieldsDialog } from './ExportFieldsDialog';
import { JobListFilters } from './JobListFilters';
import { JobListTabs } from './JobListTabs';
import { JobMirBadge } from './JobMirBadge';
import { NoTasksBadge } from './NoTasksBadge';
import { PSRBadge } from './PSRBadge';
import { PriorityBadge } from './PriorityBadge';
import { SortableHeader } from './SortableHeader';

interface JobListProps {
  onNavigateToDetail?: (orderId: string) => void;
}

export function JobList({ onNavigateToDetail }: JobListProps) {
  const [filters, setFilters] = useUrlFilters(JOB_LIST_DEFAULTS);

  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [stepperMode, setStepperMode] = useState<BulkActionMode | null>(null);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('csv');
  const [exportFields, setExportFields] = useState<JobExportField[]>([]);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [exportTemplates, setExportTemplates] = useState<ExportTemplate[]>([]);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [requestMoreInfoModalOpen, setRequestMoreInfoModalOpen] = useState(false);
  const [resultModalState, setResultModalState] = useState<
    | {
        open: true;
        actionLabel: string;
        results: BulkActionResponse['results'];
        jobsLookup: { id: number; name: string | null }[];
      }
    | { open: false }
  >({ open: false });

  const { user } = useAuth();
  const { counts } = useJobTabCounts();

  const acceptWorkOrders = useAcceptWorkOrders();
  const rejectWorkOrders = useRejectWorkOrders();
  const requestMoreInfoWorkOrders = useRequestMoreInfoWorkOrders();
  const replyMoreInfoWorkOrders = useReplyMoreInfoWorkOrders();

  const activeTab = filters.tab;
  const currentPage = parseInt(filters.page, 10) || 1;
  const priorityFilter = filters.priority_id;
  const statusFilter = filters.work_order_status_id;
  const jobTypeFilter = filters.job_category_id;
  const psrFilter = filters.psr;
  const searchTerm = filters.search_term;

  const { data: statusData } = useWorkOrderStatusList();
  // Empty string = unsorted (backend falls back to created_time DESC).
  const sortBy = filters.sort_by as JobListSortBy | '';
  const sortOrder = filters.sort_order as JobListSortOrder | '';

  const ariaSortFor = (column: JobListSortBy): 'ascending' | 'descending' | 'none' => {
    if (sortBy !== column) return 'none';
    return sortOrder === 'asc' ? 'ascending' : 'descending';
  };

  // A tab filters by EITHER work-order status names OR a MIR direction, never
  // both — "More Info Requested" has no status, so it filters on the outbound
  // pending MIR instead (see getTabFilter / tabConfig).
  const tabFilter = useMemo(() => getTabFilter(activeTab), [activeTab]);

  // The user-facing Status filter (WRK-5781) shares the `work_order_status_name`
  // param with the tab filter, so it is only applied on tabs that impose no
  // status of their own (the "All" and MIR tabs). On a status-defined tab the
  // tab already IS the status filter, so the selection is ignored here (and the
  // control is hidden in JobListFilters). The backend filters by status NAME,
  // so map the selected ids to names.
  const statusNameFilter = useMemo((): string[] | undefined => {
    if (tabImposesStatus(activeTab) || statusFilter === 'all') return undefined;
    const ids = statusFilter.split(',').filter(Boolean);
    const names = ids
      .map((id) => statusData?.find((s) => String(s.id) === id)?.name)
      .filter((name): name is string => Boolean(name));
    return names.length > 0 ? names : undefined;
  }, [activeTab, statusFilter, statusData]);

  // Filters common to the list query and the export, excluding pagination.
  // The export reuses these exact filters so the file matches the on-screen
  // result set (WRK data-export ticket, AC7 "filter applied before export").
  // `tabFilter` is spread last so a status-defined tab always wins the shared
  // `work_order_status_name` param (on those tabs statusNameFilter is undefined).
  const activeFilters = useMemo<JobListFilterParams>(
    () => ({
      priority_id:
        priorityFilter !== 'all'
          ? priorityFilter
              .split(',')
              .filter(Boolean)
              .map((id) => parseInt(id, 10))
          : undefined,
      job_category_id: jobTypeFilter !== 'all' ? parseInt(jobTypeFilter, 10) : undefined,
      is_vulnerable: psrFilter === 'true' ? true : undefined,
      search_term: searchTerm !== '' ? searchTerm : undefined,
      sort_by: sortBy !== '' ? sortBy : undefined,
      sort_order: sortOrder !== '' ? sortOrder : undefined,
      work_order_status_name: statusNameFilter,
      ...tabFilter,
    }),
    [
      priorityFilter,
      jobTypeFilter,
      psrFilter,
      searchTerm,
      sortBy,
      sortOrder,
      statusNameFilter,
      tabFilter,
    ],
  );

  const {
    data: jobListData,
    isLoading,
    isError,
    refetch: refetchJobList,
  } = useJobList({
    limit: JOBS_PAGE_SIZE,
    skip: (currentPage - 1) * JOBS_PAGE_SIZE,
    ...activeFilters,
  });

  const workOrders = useMemo((): WorkOrder[] => jobListData?.data ?? [], [jobListData]);
  const totalCount = jobListData?.total ?? 0;

  // Rows are rendered in the order the backend returns them (no client-side sort).
  const filteredOrders = workOrders;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders(new Set(filteredOrders.map((o) => String(o.id))));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    const newSelected = new Set(selectedOrders);
    if (checked) {
      newSelected.add(orderId);
    } else {
      newSelected.delete(orderId);
    }
    setSelectedOrders(newSelected);
  };

  // Three-state toggle on the same column (asc → desc → cleared); a new column
  // starts at asc. Always reset to page 1 so the user never lands on an
  // out-of-range page after the result set is re-ordered.
  const handleSort = (column: JobListSortBy) => {
    if (sortBy !== column) {
      setFilters({ sort_by: column, sort_order: 'asc', page: '1' });
    } else if (sortOrder === 'asc') {
      setFilters({ sort_by: column, sort_order: 'desc', page: '1' });
    } else {
      // Was desc → clear back to default ordering.
      setFilters({ sort_by: '', sort_order: '', page: '1' });
    }
  };

  const handleAccept = () => setAcceptModalOpen(true);
  const handleReject = () => setStepperMode('reject');
  const handleRequestInfo = () => setRequestMoreInfoModalOpen(true);

  // Open the column picker for the chosen format. Fields are fetched lazily on
  // first open so the list page doesn't pay for them until an export is wanted.
  const openExportDialog = async (format: 'csv' | 'xlsx') => {
    if (totalCount === 0) {
      showErrorToast('Nothing to export', 'No jobs match the current filters.');
      return;
    }
    setExportFormat(format);
    setExportDialogOpen(true);

    if (exportFields.length === 0) {
      setIsLoadingFields(true);
      try {
        // Templates are best-effort: a failure there must not block the export,
        // so they are fetched alongside but handled separately.
        const [fields, templates] = await Promise.all([
          getJobExportFields(),
          getExportTemplates().catch(() => [] as ExportTemplate[]),
        ]);
        setExportFields(fields);
        setExportTemplates(templates);
      } catch (error) {
        console.error('Error loading export fields:', error);
        showErrorToast('Could not load columns', 'Please try again.');
        setExportDialogOpen(false);
      } finally {
        setIsLoadingFields(false);
      }
    }
  };

  const handleSaveTemplate = async (name: string, fields: string[], isShared: boolean) => {
    try {
      const created = await createExportTemplate({
        name,
        export_type: 'job_list',
        format: exportFormat,
        fields,
        filters: activeFilters,
        is_shared: isShared,
      });
      setExportTemplates((prev) => [...prev, created]);
      showSuccessToast('Template saved', `"${name}" is ready to reuse.`);
    } catch (error) {
      console.error('Error saving export template:', error);
      showErrorToast('Could not save template', 'Please try again.');
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    try {
      await deleteExportTemplate(id);
      setExportTemplates((prev) => prev.filter((t) => t.id !== id));
      showSuccessToast('Template deleted');
    } catch (error) {
      console.error('Error deleting export template:', error);
      showErrorToast('Could not delete template', 'Please try again.');
    }
  };

  // Export every job matching the current filters (all pages, not just the one
  // on screen), limited to the chosen columns. Reuses `activeFilters` so the
  // file reflects the same result set the user is looking at.
  const handleExport = async (fields: string[]) => {
    setIsExporting(true);
    try {
      // The file is built server-side from the same filters the list uses, so
      // the export covers every matching row rather than just the current page.
      const blob = await downloadJobListExport(activeFilters, exportFormat, fields);
      downloadBlob(blob, `jobs-export_${exportDateStamp()}.${exportFormat}`, blob.type);
      showSuccessToast('Export ready', 'Your download has started.');
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting jobs:', error);
      showErrorToast('Export failed', 'Could not export jobs. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const allSelected =
    filteredOrders.length > 0 && filteredOrders.every((o) => selectedOrders.has(String(o.id)));

  const selectedJobs = useMemo(
    () => filteredOrders.filter((o) => selectedOrders.has(String(o.id))),
    [filteredOrders, selectedOrders],
  );

  const handleBulkResult = (response: BulkActionResponse, actionLabel: string) => {
    const results = response.results ?? [];
    const failed = results.filter((r) => !r.success);
    const lookup = selectedJobs.map((j) => ({ id: j.id, name: j.name }));

    if (failed.length === 0) {
      const n = results.length;
      showSuccessToast(`${n} job${n === 1 ? '' : 's'} ${actionLabel.toLowerCase()}`);
      setSelectedOrders(new Set());
      setStepperMode(null);
      setAcceptModalOpen(false);
      return;
    }

    setResultModalState({
      open: true,
      actionLabel,
      results,
      jobsLookup: lookup,
    });
    setSelectedOrders(new Set());
    setStepperMode(null);
    setAcceptModalOpen(false);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Jobs"
        subtitle={`${counts.all ?? 0} total orders • Back Office Management`}
        icon={<FileText className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="px-1">
          <JobListTabs />
        </div>

        <div className="px-1 pt-6 pb-4">
          <div className="flex items-center justify-between gap-4">
            <JobListFilters />

            <div className="flex items-center gap-2">
              {selectedJobs.length === 1 && (
                <RequestMoreInfoButton
                  mirPendingDirection={selectedJobs[0].mir_pending_direction}
                  workOrderStatus={selectedJobs[0].work_order_status}
                  onClick={handleRequestInfo}
                />
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" disabled={isExporting}>
                    <Download className="w-4 h-4" />
                    {isExporting ? 'Exporting…' : 'Export'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openExportDialog('csv')}>
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openExportDialog('xlsx')}>
                    Export as Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {clientConfig.features.bulkActions && (
                <BulkActionsBar
                  selectedJobs={selectedJobs}
                  onAccept={handleAccept}
                  onReject={handleReject}
                />
              )}
            </div>
          </div>
        </div>

        <div className="px-1 pb-6">
          <div className="bg-card border-t-2 border-border overflow-hidden">
            <Table className="[&_th]:h-11 [&_td]:py-2">
              <TableHeader className="sticky top-0 bg-card/90 backdrop-blur-sm z-10">
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="w-8">
                    <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />
                  </TableHead>
                  <TableHead aria-sort={ariaSortFor('id')}>
                    <SortableHeader
                      column="id"
                      label="Works Job ID"
                      activeColumn={sortBy}
                      activeOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                  {clientConfig.features.showApplicationReference && (
                    <TableHead className="text-foreground font-semibold text-nowrap">
                      Application Reference
                    </TableHead>
                  )}
                  <TableHead className="text-foreground font-semibold">Job Title</TableHead>
                  {/* Job Type & Status sort by their internal id (job_category_id /
                      work_order_status_id), not the display label — the backend has
                      no label-sort column. Records still group consistently. */}
                  <TableHead aria-sort={ariaSortFor('job_category_id')}>
                    <SortableHeader
                      column="job_category_id"
                      label="Job Type"
                      activeColumn={sortBy}
                      activeOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead aria-sort={ariaSortFor('priority_id')}>
                    <SortableHeader
                      column="priority_id"
                      label="Priority"
                      activeColumn={sortBy}
                      activeOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead aria-sort={ariaSortFor('work_order_status_id')}>
                    <SortableHeader
                      column="work_order_status_id"
                      label="Status"
                      activeColumn={sortBy}
                      activeOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead className="text-foreground font-semibold">Location</TableHead>
                  <TableHead className="text-foreground font-semibold">Duration</TableHead>
                  <TableHead aria-sort={ariaSortFor('req_completion_date')} className="text-nowrap">
                    <SortableHeader
                      column="req_completion_date"
                      label="Required By"
                      activeColumn={sortBy}
                      activeOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead className="text-foreground font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const isSelected = selectedOrders.has(String(order.id));

                  return (
                    <TableRow
                      key={order.id}
                      className="border-border hover:bg-accent/50 cursor-pointer"
                      onClick={() => {
                        if (!isSelected && selectedOrders.size === 0) {
                          onNavigateToDetail?.(String(order.id));
                        }
                      }}
                    >
                      <TableCell
                        className={cn('border-l-4', getStatusRowAccent(order.work_order_status))}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handleSelectOrder(String(order.id), checked === true)
                          }
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {order.id}
                      </TableCell>
                      {clientConfig.features.showApplicationReference && (
                        <TableCell className="text-sm">
                          {order.application_reference ?? '—'}
                        </TableCell>
                      )}
                      <TableCell className="font-semibold">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span>{order.name ?? '—'}</span>
                          <PSRBadge isVulnerable={order.is_vulnerable} />
                        </div>
                      </TableCell>
                      <TableCell className="text-foreground">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span>{order.job_category_name ?? '—'}</span>
                          {/* Single MIR badge derived server-side so the list and the
                              detail page cannot disagree (WRK-5381). Self-gates on the
                              feature flag and on terminal work-order statuses
                              (Completed/Rejected/Cancelled/Validated — WRK-5571). */}
                          <JobMirBadge
                            mirBadgeState={order.mir_badge_state}
                            workOrderStatus={order.work_order_status}
                          />
                          {/* Not gated on canRequestMoreInfo: a scheduled job with
                              no tasks is broken in any non-terminal status, and the
                              badge self-gates on is_scheduled (WRK-5792). */}
                          <NoTasksBadge
                            hasTasks={order.has_tasks}
                            isScheduled={order.is_scheduled}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <PriorityBadge priority={order.priority_name} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {order.work_order_status ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {order.address ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatMinutesCompact(order.duration)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-nowrap">
                        {formatRequiredBy(order.req_completion_date)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-8 p-0"
                          onClick={() => onNavigateToDetail?.(String(order.id))}
                        >
                          <Eye className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {isLoading && (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
                  <p>Loading jobs...</p>
                </div>
              </div>
            )}
            {isError && !isLoading && (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400 opacity-70" />
                  <p>Failed to load jobs</p>
                </div>
              </div>
            )}
            {!isLoading && !isError && filteredOrders.length === 0 && (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <SearchIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No jobs found</p>
                </div>
              </div>
            )}
          </div>

          {totalCount > 0 && (
            <Pagination
              page={currentPage}
              totalItems={totalCount}
              pageSize={JOBS_PAGE_SIZE}
              itemNoun="orders"
              onPageChange={(p) => setFilters({ page: String(p) })}
            />
          )}
        </div>

        <ExportFieldsDialog
          open={exportDialogOpen}
          onOpenChange={setExportDialogOpen}
          fields={exportFields}
          isLoading={isLoadingFields}
          isExporting={isExporting}
          onConfirm={handleExport}
          templates={exportTemplates}
          onSaveTemplate={handleSaveTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          currentUserId={user?.id}
        />

        <RequestMoreInfoOrReplyModal
          open={requestMoreInfoModalOpen}
          onOpenChange={setRequestMoreInfoModalOpen}
          job={selectedJobs.length === 1 ? selectedJobs[0] : null}
          isSubmitting={
            selectedJobs.length === 1 && selectedJobs[0].mir_pending_direction === 'inbound'
              ? replyMoreInfoWorkOrders.isPending
              : requestMoreInfoWorkOrders.isPending
          }
          onSubmit={(comment, files) => {
            if (selectedJobs.length !== 1) return;
            const filesMeta = files.map((f) => ({ name: f.name, size: f.size, type: f.type }));
            if (filesMeta.length > 0) {
              console.log('[request-more-info] attachments (not yet sent to backend):', filesMeta);
            }
            const payload = [
              {
                id: Number(selectedJobs[0].id),
                externalId: selectedJobs[0].external_id ?? '',
                comments: comment,
              },
            ];
            if (selectedJobs[0].mir_pending_direction === 'inbound') {
              replyMoreInfoWorkOrders.mutate(payload, {
                onSuccess: (resp) => {
                  setRequestMoreInfoModalOpen(false);
                  handleBulkResult(resp, 'Reply sent');
                  refetchJobList();
                },
                onError: (error) => {
                  setRequestMoreInfoModalOpen(false);
                  const message =
                    error instanceof Error ? error.message : 'An unexpected error occurred';
                  handleBulkResult(
                    { results: [{ id: selectedJobs[0].id, success: false, message }] },
                    'Reply sent',
                  );
                },
              });
            } else {
              requestMoreInfoWorkOrders.mutate(payload, {
                onSuccess: (resp) => {
                  setRequestMoreInfoModalOpen(false);
                  handleBulkResult(resp, 'Info request sent');
                  refetchJobList();
                },
                onError: (error) => {
                  setRequestMoreInfoModalOpen(false);
                  const message =
                    error instanceof Error ? error.message : 'An unexpected error occurred';
                  handleBulkResult(
                    { results: [{ id: selectedJobs[0].id, success: false, message }] },
                    'Info request sent',
                  );
                },
              });
            }
          }}
        />

        <BulkActionStepperModal
          open={stepperMode !== null}
          onOpenChange={(open) => {
            if (!open) setStepperMode(null);
          }}
          mode={stepperMode ?? 'reject'}
          jobs={selectedJobs.map((j) => ({
            id: j.id,
            name: j.name,
            address: j.address,
            work_order_status: j.work_order_status,
            req_completion_date: j.req_completion_date,
          }))}
          isSubmitting={isUploadingFiles || rejectWorkOrders.isPending}
          onSubmit={async (items: BulkActionStepperSubmitItem[]) => {
            if (stepperMode !== 'reject') return;

            setIsUploadingFiles(true);
            try {
              // Upload each job's supporting documents first so they persist as
              // job_file rows (visible in Lite) and yield the attachment refs we
              // forward to CORE on the reject event.
              const payload: BulkJobInput[] = [];
              for (const { id, comments, files } of items) {
                const job = selectedJobs.find((j) => j.id === id);
                const attachments = files.length > 0 ? await uploadJobDocuments(id, files) : [];
                payload.push({ id, externalId: job?.external_id ?? '', comments, attachments });
              }

              rejectWorkOrders.mutate(payload, {
                onSuccess: (resp) => {
                  handleBulkResult(resp, 'Rejected');
                  refetchJobList();
                },
              });
            } catch (error) {
              console.error('Error uploading bulk reject documents:', error);
              showErrorToast('Upload failed', 'Could not upload documents. Please try again.');
            } finally {
              setIsUploadingFiles(false);
            }
          }}
        />

        <Dialog open={acceptModalOpen} onOpenChange={setAcceptModalOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>
                Accept {selectedOrders.size} job{selectedOrders.size === 1 ? '' : 's'}?
              </DialogTitle>
              <DialogDescription>
                This moves the selected jobs out of the "new" status.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAcceptModalOpen(false)}
                disabled={acceptWorkOrders.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                disabled={acceptWorkOrders.isPending}
                onClick={() => {
                  const ids = selectedJobs.map((j) => Number(j.id));
                  acceptWorkOrders.mutate(ids, {
                    onSuccess: (resp) => {
                      handleBulkResult(resp, 'Accepted');
                      refetchJobList();
                    },
                  });
                }}
              >
                {acceptWorkOrders.isPending ? 'Accepting…' : 'Accept'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {resultModalState.open && (
          <BulkActionResultModal
            open={resultModalState.open}
            onOpenChange={(open) => {
              if (!open) setResultModalState({ open: false });
            }}
            actionLabel={resultModalState.actionLabel}
            results={resultModalState.results}
            jobsLookup={resultModalState.jobsLookup}
          />
        )}
      </div>
    </div>
  );
}
